import * as THREE from 'three';
import type { SpellSchool } from '../../../content/spells/schools.js';
import type { UnitAnimator } from '../entities/unit-animator.js';
import { SpellChannelVisual } from './spell-channel.js';
import type { SpellLight } from './spell-effects.js';

/**
 * How big a metre is on screen at `point`, in pixels, for a canvas `height` pixels tall: the scale the small lights are sized by. An orthographic
 * camera has one scale everywhere; a perspective one, the scale at the point's distance.
 */
export function pixelsPerMeter(camera: THREE.Camera, height: number, point: THREE.Vector3): number {
  if (camera instanceof THREE.OrthographicCamera) return height / ((camera.top - camera.bottom) / camera.zoom);
  if (camera instanceof THREE.PerspectiveCamera) return height / (2 * Math.max(.1, camera.position.distanceTo(point)) * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2));
  return height / 14;
}

/** Real seconds the singularity takes to let go once the cast has ended: the flash in which a spell takes its place. */
export const RELEASE_SECONDS = .18;

/**
 * How strong a full orb's light is (candela, like the lamps and braziers of the map) and over what distance in metres, and how many lights a tool
 * that has no settings (the explorer) makes.
 *
 * The lights are made up front, one for each caster the scene can have, and stay in the scene, dark when unused: three.js recompiles every lit
 * material when the number of lights in a scene changes, so a light added when a cast began would stall the game for the length of that cast's
 * start. The game sizes the pool to its casters, capped by the player's **Spell lights** setting (`graphics-options.ts`); with more casting orbs than
 * lights, the brightest orbs get them and the rest give only their glow.
 */
export const ORB_LIGHTS = 4, ORB_LIGHT_INTENSITY = 7, ORB_LIGHT_RANGE = 14;

/** How many lights a scene with `casters` casters gets under the player's limit (`Infinity` for no limit): one each, no more than the limit. */
export const orbLightCount = (limit: number, casters: number) => Math.max(0, Math.min(limit, casters));

/** What a spell needs to take the singularity's place: where it is (the world), and the school it was channeled in. */
export interface Singularity { id: string; school: SpellSchool; position: THREE.Vector3 }

interface Channel { visual: SpellChannelVisual; position: THREE.Vector3; time: number; size: number; progress: number; endedAt?: number }

/**
 * The orbs of every spell being channeled in the scene. Each frame it reads which animators are channeling (the server's cast state, seen through
 * the animator) and keeps one `SpellChannelVisual` per caster at the point between the caster's hands.
 *
 * The orb ends in the caster's hands: over the last 0.6 s of the cast (`COLLAPSE_SECONDS`, in `channel-pose.ts`) they close in and crush it, spinning ever
 * faster, into a singularity, so it is a point of light when the cast ends. That is the moment the spell is made. The point lets go in a flash (the
 * caster's hands thrust the spell away at the same moment), and `handoff` says so: it is called with the point and the school as it lets go, so a
 * spell's own effect can begin there. The release runs on real time, so it is seen even while the game is paused.
 */
export class SpellChannels {
  private readonly channels = new Map<string, Channel>();
  private readonly castProgress = new Map<string, number>();
  /** The orbs' lights: part of the scene from the start, dark until an orb is given one. */
  private readonly lights: THREE.PointLight[] = [];
  /** Called once when a visible cast begins, alongside its school visual. Late reveals stay silent. */
  onStart?: (id: string, school: SpellSchool, seconds: number) => void;
  onUpdate?: (id: string, elapsed: number, duration: number) => void;
  onStop?: (id: string, completed?: boolean) => void;
  constructor(private readonly scene: THREE.Scene, private readonly handoff?: (end: Singularity) => void, lights = ORB_LIGHTS) { this.setLights(lights); }

  /** How many lights there are. Changing it changes the number of lights in the scene, which makes the renderer recompile every lit material once. */
  get lightCount() { return this.lights.length; }
  get visualObjects(): THREE.Object3D[] { return [...this.channels.values()].map(channel => channel.visual.group); }
  get lightObjects(): readonly THREE.PointLight[] { return this.lights; }
  setLights(count: number) {
    count = Math.max(0, Math.floor(count));
    while (this.lights.length < count) {
      const light = new THREE.PointLight(0xffffff, 0, ORB_LIGHT_RANGE, 2); light.name = `orb-light-${this.lights.length}`;
      this.scene.add(light); this.lights.push(light);
    }
    while (this.lights.length > count) this.lights.pop()!.removeFromParent();
  }

  /** `pixelsPerMeter` is how large a metre is on screen, which sizes the small lights (the camera is orthographic). */
  update(animators: ReadonlyMap<string, UnitAnimator>, visible: (id: string) => boolean, now: number, pixelsPerMeter: number, spellLights: readonly SpellLight[] = []) {
    for (const [id, animator] of animators) {
      const cast = animator.channelOrb?.(), previous = this.castProgress.get(id);
      const starting = !!cast && (previous === undefined || cast.progress < previous);
      if (cast) this.castProgress.set(id, cast.progress); else this.castProgress.delete(id);
      const orb = visible(id) ? cast : undefined;
      let channel = this.channels.get(id);
      if (!orb) { this.onStop?.(id); if (channel && channel.endedAt === undefined) channel.endedAt = now; continue; }
      if (!channel || channel.endedAt !== undefined || channel.visual.school.id !== orb.school.id) {
        // A new cast replaces whatever was still collapsing from the last one.
        if (channel) channel.visual.dispose();
        channel = { visual: new SpellChannelVisual(orb.school, id.length), position: new THREE.Vector3(), time: 0, size: 0, progress: 0 };
        this.scene.add(channel.visual.group); this.channels.set(id, channel);
      }
      if (starting && orb.progress <= .15) this.onStart?.(id, orb.school, orb.seconds);
      if (orb.progress >= 1) this.onStop?.(id, true);
      else this.onUpdate?.(id, orb.progress * orb.seconds, orb.seconds);
      channel.position.copy(orb.position); channel.time = orb.progress * orb.seconds; channel.size = orb.size; channel.progress = orb.progress;
      channel.visual.group.position.copy(orb.position);
      channel.visual.update(channel.time, channel.size, orb.position.y, pixelsPerMeter, orb.collapse ?? 0, 0, orb.birth, orb.sincePop);
    }
    for (const [id, channel] of this.channels) {
      if (channel.endedAt === undefined && animators.has(id)) continue;
      const elapsed = (now - (channel.endedAt ?? now)) / 1000;
      if (elapsed >= RELEASE_SECONDS || !animators.has(id)) {
        // A figure that is gone takes its orb with it; one whose cast ran its course hands the singularity on.
        // (A cast that was cut short, its caster felled, ends the same way but hands nothing on.)
        if (animators.has(id) && channel.endedAt !== undefined && channel.progress >= .9) this.handoff?.({ id, school: channel.visual.school, position: channel.position.clone() });
        channel.visual.dispose(); this.channels.delete(id); continue;
      }
      // The cast's own clock stops with the cast, but the orb's does not: it is a point of light, still turning, that lets go.
      channel.visual.update(channel.time + elapsed, channel.size, channel.position.y, pixelsPerMeter, 1, Math.min(1, elapsed / RELEASE_SECONDS));
    }
    this.light(spellLights);
  }

  /** Hands the lights to the brightest orbs, at the orbs, in their colours; every light left over goes dark. */
  private light(spellLights: readonly SpellLight[]) {
    const lit = [...this.channels.values()].map(channel => ({ position: channel.position, color: channel.visual.lightColor, output: channel.visual.output }))
      .concat(spellLights).filter(light => light.output > .01).sort((a, b) => b.output - a.output);
    this.lights.forEach((light, i) => {
      const channel = lit[i];
      if (!channel) { light.intensity = 0; return; }
      light.position.copy(channel.position); light.color.copy(channel.color); light.intensity = ORB_LIGHT_INTENSITY * channel.output;
    });
  }

  /** Removes one caster's orb at once (the unit was removed). */
  remove(id: string) { this.onStop?.(id); this.channels.get(id)?.visual.dispose(); this.channels.delete(id); this.castProgress.delete(id); }
  clear() { for (const [id, channel] of this.channels) { this.onStop?.(id); channel.visual.dispose(); } this.channels.clear(); this.castProgress.clear(); for (const light of this.lights) light.intensity = 0; }
  /** Takes everything this made out of the scene, lights included. */
  dispose() { this.clear(); this.setLights(0); }
}
